import { useEffect, useRef } from 'react'
import type { FC } from 'react'
import { useCookies } from '~/features/cookies'
import { pushToDataLayer } from '~/features/infra/gtm'
import type { CookiePreferences } from '~/features/cookies/preferences'

const toConsentFlags = (preferences: CookiePreferences): Record<string, string> => {
  const entries = Object.entries(preferences).map(([key, value]) => [
    `${key}Cookies`,
    value ? 'granted' : 'denied'
  ])

  return Object.fromEntries(entries)
}

const serialiseConsent = (consent: Record<string, string>): string => {
  const sorted = Object.entries(consent).sort(([a], [b]) => a.localeCompare(b))

  return JSON.stringify(sorted)
}

const CookieDataLayer: FC = () => {
  const { cookiePreferences, preferenceIsSet } = useCookies()
  const hasEmittedLoad = useRef(false)
  const lastPushedRef = useRef<string | null>(null)

  useEffect(() => {
    if (Object.keys(cookiePreferences).length === 0) {
      return
    }

    const consent = toConsentFlags(cookiePreferences)
    const serialised = serialiseConsent(consent)

    if (serialised === lastPushedRef.current) {
      return
    }

    lastPushedRef.current = serialised

    if (!hasEmittedLoad.current) {
      hasEmittedLoad.current = true

      if (preferenceIsSet('essential')) {
        pushToDataLayer({ event: 'consent_preferences', ...consent })
      }

      return
    }

    pushToDataLayer({ event: 'consent_update', ...consent })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookiePreferences])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>
}

export { CookieDataLayer }
